import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Receipt,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Download,
  FileSpreadsheet,
  FileText,
  Printer,
  AlertTriangle,
  Users,
  CreditCard,
  Banknote,
  Coins,
  ExternalLink,
  Layers,
  Lock,
  Unlock,
  AlertCircle,
  FileCheck2
} from 'lucide-react';
import { reportesAPI, describirError } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ToastProvider';
import { formatoMoneda } from '../../utils/finanzas';
import { calcularParametrosPeriodo } from './cierreCajaPeriodo';
import { limpiarParametros, nombreArchivoCierre } from './cierreCajaDescarga';
import { guardarBlob, imprimirBlobPdf, obtenerArchivoAutenticado } from '../../utils/descargaAutenticada';
import '../../styles/CierreCaja.css';

const PRESETS = [
  { id: 'DIA_ESPECIFICO', label: 'Día Específico' },
  { id: 'HOY', label: 'Hoy' },
  { id: 'AYER', label: 'Ayer' },
  { id: 'ESTA_SEMANA', label: 'Esta Semana' },
  { id: 'RANGO_PERSONALIZADO', label: 'Rango Personalizado' }
];

// Fecha operativa de Ciudad de México (no la fecha UTC del navegador): después de las 18:00 locales
// la fecha UTC ya es mañana y se podría cerrar un día futuro.
const fechaOperativaMX = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' }).format(new Date());

export default function CierreCaja() {
  const navigate = useNavigate();
  const toast = useToast();
  // Reapertura (P08-B0.5): solo con el permiso explícito (ADMIN pasa por contingencia). El backend
  // revalida autenticación y permiso; esto solo decide qué se muestra.
  const { hasPermission, user } = useAuth();
  const puedeReabrirPermiso = hasPermission('CIERRE_CAJA_REABRIR');
  // Cerrar caja exige CIERRE_CAJA_CERRAR (el backend lo revalida; esto solo oculta el botón).
  const puedeCerrarPermiso = hasPermission('CIERRE_CAJA_CERRAR');
  const esAdmin = user?.rol?.codigo === 'ADMIN';
  // Segregación: nadie reabre su propia caja salvo contingencia ADMIN (el backend lo exige igual).
  const puedeReabrirEste = (cierre) => puedeReabrirPermiso && cierre?.estado === 'CERRADO' && (esAdmin || String(cierre.cajeroId) !== String(user?.id));

  // Navegación de Contextos
  const [tabActiva, setTabActiva] = useState('CONSULTA'); // 'CONSULTA' | 'MI_CIERRE'

  // ==========================================
  // CONTEXTO 1: CONSULTA GENERAL (FASE 2B)
  // ==========================================
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // --------------------------------------------------------------------
  // Selector de periodo: ÚNICA fuente de verdad = periodoPreset + (para
  // DIA_ESPECIFICO/RANGO_PERSONALIZADO) la fecha que el usuario escribió.
  // Las fechas EFECTIVAS que se muestran en pantalla para HOY/AYER/
  // ESTA_SEMANA nunca se calculan aquí: se toman de data.corte, que es lo
  // que el backend (cierreCajaService.resolverFechasCierre, con la zona
  // horaria de src/config/fechas.js) ya resolvió para esa consulta. Esto
  // evita duplicar lógica de fechas/zona horaria en el cliente — el mismo
  // principio que ya usa el resto de MIDAS ("React nunca convierte fechas
  // para decidir en qué periodo cae un registro").
  // --------------------------------------------------------------------
  const [periodoPreset, setPeriodoPreset] = useState('DIA_ESPECIFICO');
  const [fechaEspecifica, setFechaEspecifica] = useState('');
  const [rangoPersonalizado, setRangoPersonalizado] = useState({ desde: '', hasta: '' });

  const [formaPago, setFormaPago] = useState('TODAS');
  const [registradoPor, setRegistradoPor] = useState('TODOS');
  const [estado, setEstado] = useState('APLICADO');
  const [search, setSearch] = useState('');

  const [expandedRows, setExpandedRows] = useState(new Set());
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);

  // ==========================================
  // CONTEXTO 2: MI CIERRE FÍSICO (FASE 2C)
  // ==========================================
  const hoyISO = fechaOperativaMX();
  const [fechaMiCierre, setFechaMiCierre] = useState(hoyISO);
  const [miCierreData, setMiCierreData] = useState(null);
  const [loadingMiCierre, setLoadingMiCierre] = useState(false);

  const [efectivoDeclarado, setEfectivoDeclarado] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [cierreModalOpen, setCierreModalOpen] = useState(false);
  const [cierreAReabrir, setCierreAReabrir] = useState(null); // cierre CERRADO que se va a reabrir (modal)
  const [motivoReapertura, setMotivoReapertura] = useState('');
  const [observacionesReapertura, setObservacionesReapertura] = useState('');
  const [supervision, setSupervision] = useState({ lista: [], cargando: false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    function handleClickOutside(event) {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setExportOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Parámetros de fecha que de verdad se envían a la API, derivados del
  // periodo seleccionado. Para HOY/AYER/ESTA_SEMANA no se manda ninguna
  // fecha: el backend las resuelve por su cuenta (y las ignora si se le
  // mandan), así que no hay nada que pueda quedar desincronizado.
  const parametrosPeriodo = useMemo(
    () => calcularParametrosPeriodo(periodoPreset, { fechaEspecifica, rangoPersonalizado }),
    [periodoPreset, fechaEspecifica, rangoPersonalizado]
  );

  // Fechas EFECTIVAS mostradas en pantalla: siempre las últimas que el
  // servidor confirmó para la consulta vigente (data.corte), nunca un
  // cálculo propio del navegador.
  const fechaDesdeEfectiva = data?.corte?.fechaDesde || '';
  const fechaHastaEfectiva = data?.corte?.fechaHasta || '';

  // Carga Consulta General
  const cargarCierre = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        periodoPreset,
        ...parametrosPeriodo,
        formaPago: formaPago !== 'TODAS' ? formaPago : undefined,
        registradoPor: registradoPor !== 'TODOS' ? registradoPor : undefined,
        estado: estado !== 'TODOS' ? estado : undefined,
        search: search.trim() || undefined
      };

      const res = await reportesAPI.cierreCaja(params);
      setData(res.data);
    } catch (err) {
      const errInfo = describirError(err);
      toast.error(errInfo.mensaje || 'Error al consultar el cierre de caja.');
    } finally {
      setLoading(false);
    }
  }, [periodoPreset, parametrosPeriodo, formaPago, registradoPor, estado, search, toast]);

  // Carga Mi Cierre
  const cargarMiCierre = useCallback(async () => {
    setLoadingMiCierre(true);
    try {
      const res = await reportesAPI.obtenerMiCierre({ fecha: fechaMiCierre });
      setMiCierreData(res.data);
      if (res.data?.estado === 'CERRADO') {
        setEfectivoDeclarado(String(res.data.efectivoDeclarado || ''));
        setObservaciones(res.data.observaciones || '');
      } else if (res.data?.estado === 'REABIERTO') {
        setEfectivoDeclarado('');
        setObservaciones('');
      } else {
        setEfectivoDeclarado('');
        setObservaciones('');
      }
    } catch (err) {
      const errInfo = describirError(err);
      toast.error(errInfo.mensaje || 'Error al obtener la información de Mi Cierre.');
    } finally {
      setLoadingMiCierre(false);
    }
  }, [fechaMiCierre, toast]);

  useEffect(() => {
    if (tabActiva === 'CONSULTA') {
      cargarCierre();
    } else {
      cargarMiCierre();
    }
  }, [tabActiva, cargarCierre, cargarMiCierre]);

  const handleCambiarPreset = (presetId) => {
    setPeriodoPreset(presetId);
    // Al entrar a Rango Personalizado desde otro preset, arrancar el rango
    // con las últimas fechas efectivas conocidas (no vacío, no "hoy" del
    // navegador) para que el usuario ajuste desde un punto de partida real.
    if (presetId === 'RANGO_PERSONALIZADO' && !rangoPersonalizado.desde && !rangoPersonalizado.hasta) {
      setRangoPersonalizado({ desde: fechaDesdeEfectiva, hasta: fechaHastaEfectiva });
    }
  };

  const toggleRow = (paymentId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(paymentId)) {
        next.delete(paymentId);
      } else {
        next.add(paymentId);
      }
      return next;
    });
  };

  // Excel, PDF e impresión reutilizan literalmente los mismos parámetros
  // (periodoPreset + parametrosPeriodo) que la consulta en pantalla, para
  // garantizar que exportan exactamente el periodo y filtros mostrados.
  //
  // Las descargas usan apiClient (Bearer) con responseType 'blob' mediante el
  // módulo compartido utils/descargaAutenticada: el token nunca viaja en la
  // URL, se valida que la respuesta sea el archivo esperado y se liberan los
  // recursos temporales.
  const [descargando, setDescargando] = useState(null); // null | 'excel' | 'pdf' | 'imprimir' | 'comprobante'

  const obtenerArchivoCierre = (tipo) => {
    const params = limpiarParametros({
      periodoPreset,
      ...parametrosPeriodo,
      formaPago,
      registradoPor,
      estado,
      search
    });
    return obtenerArchivoAutenticado(tipo, () => reportesAPI.descargarCierreCajaArchivo(tipo, params));
  };

  const exportarArchivo = async (tipo) => {
    if (descargando) return;
    setExportOpen(false);
    setDescargando(tipo);
    try {
      const nombre = nombreArchivoCierre(tipo, fechaDesdeEfectiva, fechaHastaEfectiva);
      const blob = await obtenerArchivoCierre(tipo);
      guardarBlob(blob, nombre);
    } catch (err) {
      toast.error(err.message || 'No se pudo descargar el archivo.');
    } finally {
      setDescargando(null);
    }
  };

  const exportarExcel = () => exportarArchivo('excel');
  const exportarPdf = () => exportarArchivo('pdf');

  // Impresión formal: imprime el MISMO PDF autenticado del reporte (no la
  // pantalla).
  const handlePrint = async () => {
    if (descargando) return;
    setExportOpen(false);
    setDescargando('imprimir');
    try {
      nombreArchivoCierre('pdf', fechaDesdeEfectiva, fechaHastaEfectiva); // exige periodo resuelto
      imprimirBlobPdf(await obtenerArchivoCierre('pdf'));
    } catch (err) {
      toast.error(err.message || 'No se pudo preparar la impresión.');
    } finally {
      setDescargando(null);
    }
  };

  // Cálculos dinámicos para Mi Cierre
  const decEsperado = miCierreData ? Number(miCierreData.efectivoEsperado || 0) : 0;
  const decDeclarado = efectivoDeclarado !== '' && !isNaN(Number(efectivoDeclarado)) ? Number(efectivoDeclarado) : 0;
  const decDiferencia = decDeclarado - decEsperado;

  let estadoDiferencia = 'CUADRADO';
  if (decDiferencia > 0) estadoDiferencia = 'SOBRANTE';
  else if (decDiferencia < 0) estadoDiferencia = 'FALTANTE';

  const puedeCerrar = Boolean(
    puedeCerrarPermiso &&
    miCierreData &&
    !miCierreData.isLegacyOnly &&
    miCierreData.permiteCierreFisico &&
    efectivoDeclarado !== '' &&
    !isNaN(Number(efectivoDeclarado)) &&
    Number(efectivoDeclarado) >= 0 &&
    (decDiferencia === 0 || (observaciones && observaciones.trim().length > 0))
  );

  const cargarSupervision = useCallback(async () => {
    setSupervision((p) => ({ ...p, cargando: true }));
    try {
      const res = await reportesAPI.listarCierresPersistentes({ fecha: fechaMiCierre });
      setSupervision({ lista: res.data?.data || [], cargando: false });
    } catch (err) {
      setSupervision({ lista: [], cargando: false });
      toast.error(describirError(err).mensaje || 'No se pudo cargar la supervisión de cierres.');
    }
  }, [fechaMiCierre, toast]);

  useEffect(() => {
    if (tabActiva === 'MI_CIERRE' && puedeReabrirPermiso) cargarSupervision();
  }, [tabActiva, puedeReabrirPermiso, cargarSupervision]);

  // Ejecutar Cierre Físico
  const ejecutarCerrarCaja = async () => {
    setSubmitting(true);
    try {
      await reportesAPI.cerrarCajaFisica({
        fechaOperacion: fechaMiCierre,
        efectivoDeclarado: decDeclarado,
        observaciones: observaciones?.trim() || '',
        idempotencyKey: `close-${fechaMiCierre}-${Date.now()}`
      });
      setCierreModalOpen(false);
      toast.exito('Caja cerrada.');
      cargarMiCierre();
      if (puedeReabrirPermiso) cargarSupervision();
    } catch (err) {
      const errInfo = describirError(err);
      toast.error(errInfo.mensaje || 'Error al procesar el cierre de caja.');
    } finally {
      setSubmitting(false);
    }
  };

  // Ejecutar Reapertura (el backend exige autenticación + CIERRE_CAJA_REABRIR; 401/403/409/422 se muestran como aviso)
  const ejecutarReabrirCaja = async () => {
    if (!cierreAReabrir) return;
    if (motivoReapertura.trim().length < 3) {
      toast.error('Debe capturar el motivo de la reapertura (mínimo 3 caracteres).');
      return;
    }
    setSubmitting(true);
    try {
      await reportesAPI.reabrirCajaFisica(cierreAReabrir.id, {
        motivoReapertura: motivoReapertura.trim(),
        ...(observacionesReapertura.trim() ? { observaciones: observacionesReapertura.trim() } : {})
      });
      setCierreAReabrir(null);
      setMotivoReapertura('');
      setObservacionesReapertura('');
      toast.exito('Cierre reabierto. La operación quedó auditada.');
      cargarMiCierre();
      cargarSupervision();
    } catch (err) {
      const errInfo = describirError(err);
      toast.error(errInfo.mensaje || 'Error al autorizar la reapertura.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDescargarComprobante = async (id) => {
    if (descargando) return;
    setDescargando('comprobante');
    try {
      const blob = await obtenerArchivoAutenticado('pdf', () => reportesAPI.descargarComprobanteCierrePdf(id));
      const fecha = String(miCierreData?.fecha || fechaMiCierre || '').slice(0, 10);
      guardarBlob(blob, /^\d{4}-\d{2}-\d{2}$/.test(fecha)
        ? `MIDAS_Comprobante_Cierre_Caja_${fecha}.pdf`
        : `MIDAS_Comprobante_Cierre_Caja_${id}.pdf`);
    } catch (err) {
      toast.error(err.message || 'No se pudo descargar el comprobante.');
    } finally {
      setDescargando(null);
    }
  };

  const kpis = data?.kpis || {
    totalCobrado: 0,
    totalPagos: 0,
    clientesUnicos: 0,
    creditosUnicos: 0,
    efectivoRegistrado: 0,
    noEfectivo: 0,
    noClasificado: 0
  };

  const registros = data?.registros || [];
  const desglosesFP = data?.desgloseFormasPago || [];
  const desglosesUsuarios = data?.desgloseUsuarios || [];

  const usuariosDisponibles = Array.from(
    new Set((data?.desgloseUsuarios || []).map((u) => u.registradoPor))
  );

  return (
    <div className="cierre-caja-page">
      {/* Header */}
      <div className="cierre-header">
        <div className="cierre-title-area">
          <h1>
            <Receipt size={28} color="#0284c7" />
            Cierre y Arqueo de Caja
            <span className={`cierre-title-badge ${tabActiva === 'CONSULTA' ? 'readonly' : 'primary'}`}>
              {tabActiva === 'CONSULTA' ? 'Consulta Read-Only' : 'Cierre Físico Persistente'}
            </span>
          </h1>
          <p className="cierre-subtitle">
            {tabActiva === 'CONSULTA'
              ? `Control y Conciliación General · ${
                  fechaDesdeEfectiva && fechaHastaEfectiva
                    ? (fechaDesdeEfectiva === fechaHastaEfectiva
                        ? fechaDesdeEfectiva
                        : `${fechaDesdeEfectiva} – ${fechaHastaEfectiva}`)
                    : ''
                }`
              : `Arqueo físico, declaración de efectivo y emisión de actas operativas.`}
          </p>
        </div>

        <div className="cierre-header-actions">
          <button
            className="btn-cierre-action"
            onClick={tabActiva === 'CONSULTA' ? cargarCierre : cargarMiCierre}
            disabled={loading || loadingMiCierre}
            title="Actualizar datos"
          >
            <RefreshCw size={15} className={(loading || loadingMiCierre) ? 'spin' : ''} />
            Actualizar
          </button>

          {tabActiva === 'CONSULTA' && (
            <div className="export-dropdown-wrapper" ref={exportRef}>
              <button
                className="btn-cierre-action primary"
                onClick={() => setExportOpen(!exportOpen)}
                disabled={!!descargando}
                aria-busy={!!descargando}
              >
                {descargando ? <RefreshCw size={15} className="spin" /> : <Download size={15} />}
                {descargando ? 'Generando…' : 'Exportar'}
                <ChevronDown size={14} />
              </button>

              {exportOpen && (
                <div className="export-menu">
                  <button className="export-menu-item" onClick={exportarExcel} disabled={loading || !!descargando}>
                    <FileSpreadsheet size={16} color="#107c41" />
                    Excel (.xlsx)
                  </button>
                  <button className="export-menu-item" onClick={exportarPdf} disabled={loading || !!descargando}>
                    <FileText size={16} color="#d83b01" />
                    PDF (.pdf)
                  </button>
                  <button className="export-menu-item" onClick={handlePrint} disabled={loading || !!descargando}>
                    <Printer size={16} color="#64748b" />
                    Imprimir
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Selector de Pestañas / Contextos */}
      <div className="cierre-nav-tabs">
        <button
          className={`cierre-nav-tab ${tabActiva === 'CONSULTA' ? 'active' : ''}`}
          onClick={() => setTabActiva('CONSULTA')}
        >
          <Layers size={16} />
          Consulta General
        </button>
        <button
          className={`cierre-nav-tab ${tabActiva === 'MI_CIERRE' ? 'active' : ''}`}
          onClick={() => setTabActiva('MI_CIERRE')}
        >
          <Lock size={16} />
          Mi Cierre de Caja
        </button>
      </div>

      {/* ========================================================================= */}
      {/* PESTAÑA 1: CONSULTA GENERAL (READ-ONLY) */}
      {/* ========================================================================= */}
      {tabActiva === 'CONSULTA' && (
        <>
          {/* Barra de Filtros y Temporalidad */}
          <div className="cierre-controls-card">
            <div className="preset-buttons-group">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  className={`preset-btn ${periodoPreset === p.id ? 'active' : ''}`}
                  onClick={() => handleCambiarPreset(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="cierre-filters-grid">
              <div className="filter-group">
                <label>Desde</label>
                <input
                  type="date"
                  className="filter-input"
                  disabled={periodoPreset === 'HOY' || periodoPreset === 'AYER' || periodoPreset === 'ESTA_SEMANA'}
                  value={
                    periodoPreset === 'RANGO_PERSONALIZADO'
                      ? rangoPersonalizado.desde
                      : periodoPreset === 'DIA_ESPECIFICO'
                        ? (fechaEspecifica || fechaDesdeEfectiva)
                        : fechaDesdeEfectiva
                  }
                  onChange={(e) => {
                    const valor = e.target.value;
                    if (periodoPreset === 'RANGO_PERSONALIZADO') {
                      setRangoPersonalizado((prev) => ({ ...prev, desde: valor }));
                    } else {
                      setPeriodoPreset('DIA_ESPECIFICO');
                      setFechaEspecifica(valor);
                    }
                  }}
                />
              </div>

              <div className="filter-group">
                <label>Hasta</label>
                <input
                  type="date"
                  className="filter-input"
                  disabled={periodoPreset === 'HOY' || periodoPreset === 'AYER' || periodoPreset === 'ESTA_SEMANA'}
                  value={
                    periodoPreset === 'RANGO_PERSONALIZADO' ? rangoPersonalizado.hasta : fechaHastaEfectiva
                  }
                  onChange={(e) => {
                    const valor = e.target.value;
                    setPeriodoPreset('RANGO_PERSONALIZADO');
                    setRangoPersonalizado((prev) => ({
                      desde: prev.desde || fechaDesdeEfectiva,
                      hasta: valor
                    }));
                  }}
                />
              </div>

              <div className="filter-group">
                <label>Forma de Pago</label>
                <select
                  className="filter-input"
                  value={formaPago}
                  onChange={(e) => setFormaPago(e.target.value)}
                >
                  <option value="TODAS">Todas las formas</option>
                  <option value="EFECTIVO">Efectivo</option>
                  <option value="TRANSFERENCIA">Transferencia</option>
                  <option value="DEPOSITO">Depósito</option>
                  <option value="OTRO">Otro</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Usuario Registrador</label>
                <select
                  className="filter-input"
                  value={registradoPor}
                  onChange={(e) => setRegistradoPor(e.target.value)}
                >
                  <option value="TODOS">Todos los usuarios</option>
                  {usuariosDisponibles.map((usr) => (
                    <option key={usr} value={usr}>
                      {usr}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Estado de Pago</label>
                <select
                  className="filter-input"
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                >
                  <option value="APLICADO">Aplicados (Válidos)</option>
                  <option value="REVERSADO">Reversados (Anulados)</option>
                  <option value="TODOS">Todos</option>
                </select>
              </div>

              <div className="filter-group search-filter">
                <label>Búsqueda</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Folio, crédito, cliente..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="cierre-kpi-grid">
            <div className="kpi-card total-recaudado">
              <div className="kpi-header">
                <span className="kpi-label">Total Cobrado</span>
                <div className="kpi-icon-wrapper blue">
                  <Banknote size={18} />
                </div>
              </div>
              <div className="kpi-value">{formatoMoneda(kpis.totalCobrado)}</div>
              <div className="kpi-footer">
                <span>{kpis.totalPagos} pagos registrados</span>
              </div>
            </div>

            <div className="kpi-card efectivo">
              <div className="kpi-header">
                <span className="kpi-label">Efectivo Registrado</span>
                <div className="kpi-icon-wrapper green">
                  <Coins size={18} />
                </div>
              </div>
              <div className="kpi-value">{formatoMoneda(kpis.efectivoRegistrado)}</div>
              <div className="kpi-footer">
                <span>
                  {kpis.totalCobrado > 0
                    ? `${((kpis.efectivoRegistrado / kpis.totalCobrado) * 100).toFixed(1)}% del total`
                    : '0.0%'}
                </span>
              </div>
            </div>

            <div className="kpi-card no-efectivo">
              <div className="kpi-header">
                <span className="kpi-label">No Efectivo (Bancos)</span>
                <div className="kpi-icon-wrapper purple">
                  <CreditCard size={18} />
                </div>
              </div>
              <div className="kpi-value">{formatoMoneda(kpis.noEfectivo)}</div>
              <div className="kpi-footer">
                <span>Transf. + Depósitos</span>
              </div>
            </div>

            <div className="kpi-card cobertura">
              <div className="kpi-header">
                <span className="kpi-label">Clientes / Créditos</span>
                <div className="kpi-icon-wrapper slate">
                  <Users size={18} />
                </div>
              </div>
              <div className="kpi-value">
                {kpis.clientesUnicos} <span className="kpi-subval">/ {kpis.creditosUnicos} crs</span>
              </div>
              <div className="kpi-footer">
                <span>Con pago en el periodo</span>
              </div>
            </div>
          </div>

          {/* Desgloses */}
          <div className="desgloses-grid">
            <div className="desglose-card">
              <div className="desglose-header">
                <h3>Desglose por Forma de Pago</h3>
                <span className="badge-count">{desglosesFP.length} formas</span>
              </div>
              <div className="desglose-table-wrapper">
                <table className="desglose-table">
                  <thead>
                    <tr>
                      <th>Forma de Pago</th>
                      <th className="text-right">Pagos</th>
                      <th className="text-right">Importe</th>
                      <th className="text-right">% Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {desglosesFP.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4 text-muted">
                          Sin transacciones
                        </td>
                      </tr>
                    ) : (
                      desglosesFP.map((fp) => (
                        <tr key={fp.formaPago}>
                          <td>
                            <span className={`fp-chip ${fp.formaPago.toLowerCase()}`}>
                              {fp.formaPago}
                            </span>
                          </td>
                          <td className="text-right">{fp.count}</td>
                          <td className="text-right font-medium">{formatoMoneda(fp.totalAmount)}</td>
                          <td className="text-right text-muted">{fp.percentageOfTotal.toFixed(2)}%</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="desglose-card">
              <div className="desglose-header">
                <h3>Desglose por Usuario Registrador</h3>
                <span className="badge-count">{desglosesUsuarios.length} usuarios</span>
              </div>
              <div className="desglose-table-wrapper">
                <table className="desglose-table">
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th className="text-right">Pagos</th>
                      <th className="text-right">Efectivo</th>
                      <th className="text-right">No Efectivo</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {desglosesUsuarios.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">
                          Sin transacciones
                        </td>
                      </tr>
                    ) : (
                      desglosesUsuarios.map((usr) => (
                        <tr key={usr.registradoPor}>
                          <td className="font-medium text-slate-800">{usr.registradoPor}</td>
                          <td className="text-right">{usr.pagos}</td>
                          <td className="text-right text-emerald-700">{formatoMoneda(usr.efectivo)}</td>
                          <td className="text-right text-indigo-700">{formatoMoneda(usr.noEfectivo)}</td>
                          <td className="text-right font-bold text-slate-900">{formatoMoneda(usr.importe)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Tabla Detalle de Pagos */}
          <div className="cierre-table-card">
            <div className="cierre-table-header">
              <div className="table-title-area">
                <h3>Detalle de Pagos Registrados</h3>
                <span className="table-subtitle">{registros.length} pagos conciliados</span>
              </div>
            </div>

            <div className="cierre-table-wrapper">
              <table className="cierre-main-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>Folio Pago</th>
                    <th>Fecha / Hora</th>
                    <th>Folio Crédito</th>
                    <th>Cliente Titular</th>
                    <th>Registrado Por</th>
                    <th>Forma Pago</th>
                    <th>Referencia</th>
                    <th className="text-right">Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {registros.length === 0 ? (
                    <tr>
                      <td colSpan="9">
                        <div className="cierre-empty-state">
                          <Receipt size={40} />
                          <h4>No se encontraron pagos</h4>
                          <p>Intente ajustando el rango de fechas o los filtros seleccionados.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    registros.map((p) => {
                      const isExpanded = expandedRows.has(p.paymentId);
                      return (
                        <React.Fragment key={p.paymentId}>
                          <tr className={`main-row ${isExpanded ? 'is-expanded' : ''}`}>
                            <td className="text-center">
                              {p.aplicaciones && p.aplicaciones.length > 0 ? (
                                <button
                                  className="row-expand-btn"
                                  onClick={() => toggleRow(p.paymentId)}
                                  title="Ver aplicaciones contables"
                                >
                                  {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                                </button>
                              ) : (
                                <span className="no-expand">-</span>
                              )}
                            </td>
                            <td>
                              <span className="folio-pago-badge">{p.folioPago}</span>
                            </td>
                            <td>
                              <div className="date-cell">
                                <span className="date-val">{p.fechaPago}</span>
                                <span className="time-val">{p.horaRegistro || '--:--'}</span>
                              </div>
                            </td>
                            <td>
                              <button
                                className="folio-credito-link"
                                onClick={() => p.creditoId && navigate(`/creditos/${p.creditoId}`)}
                              >
                                {p.folioCredito}
                                <ExternalLink size={11} />
                              </button>
                            </td>
                            <td>
                              <span className="cliente-name" title={p.nombreCliente}>
                                {p.nombreCliente}
                              </span>
                            </td>
                            <td>
                              <span className="user-badge">{p.registradoPor}</span>
                            </td>
                            <td>
                              <span className={`fp-chip small ${p.formaPago.toLowerCase()}`}>
                                {p.formaPago}
                              </span>
                            </td>
                            <td>
                              <span className="ref-text">{p.referencia || '—'}</span>
                            </td>
                            <td className="text-right font-bold text-slate-900">
                              {formatoMoneda(p.importeRecibido)}
                            </td>
                          </tr>

                          {isExpanded && p.aplicaciones && p.aplicaciones.length > 0 && (
                            <tr className="nested-details-row">
                              <td colSpan="9">
                                <div className="nested-details-box">
                                  <div className="nested-details-header">
                                    <h4>Aplicaciones a Cuotas / Moratorios</h4>
                                    <span>{p.aplicaciones.length} aplicaciones activas</span>
                                  </div>
                                  <table className="nested-app-table">
                                    <thead>
                                      <tr>
                                        <th>Tipo Destino</th>
                                        <th>Cuota #</th>
                                        <th>Vencimiento</th>
                                        <th>Orden</th>
                                        <th className="text-right">Importe Aplicado</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {p.aplicaciones.map((app) => (
                                        <tr key={app.id}>
                                          <td>
                                            <span className={`app-type-tag ${app.tipo.toLowerCase()}`}>
                                              {app.tipo}
                                            </span>
                                          </td>
                                          <td>{app.numeroPago !== null ? `#${app.numeroPago}` : 'N/A'}</td>
                                          <td>{app.fechaVencimiento || '—'}</td>
                                          <td>{app.ordenAplicacion}</td>
                                          <td className="text-right font-semibold">
                                            {formatoMoneda(app.importeAplicado)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ========================================================================= */}
      {/* PESTAÑA 2: MI CIERRE DE CAJA (FASE 2C PERSISTENTE) */}
      {/* ========================================================================= */}
      {tabActiva === 'MI_CIERRE' && (
        <div className="mi-cierre-container">
          {/* Selector de Fecha */}
          <div className="cierre-controls-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div className="filter-group">
                <label>Fecha Operativa a Evaluar</label>
                <input
                  type="date"
                  className="filter-input"
                  value={fechaMiCierre}
                  onChange={(e) => setFechaMiCierre(e.target.value)}
                />
              </div>
              <div style={{ alignSelf: 'flex-end' }}>
                <button className="btn-cierre-action" onClick={cargarMiCierre} disabled={loadingMiCierre}>
                  <RefreshCw size={15} className={loadingMiCierre ? 'spin' : ''} />
                  Consultar Caja del Día
                </button>
              </div>
            </div>
          </div>

          {/* Banner de exclusión histórica si aplica */}
          {miCierreData?.isLegacyOnly && (
            <div className="mi-cierre-banner-legacy">
              <AlertCircle size={24} color="#b45309" />
              <div>
                <strong>Aviso de Conciliación Histórica:</strong>
                <p style={{ margin: '0.25rem 0 0 0' }}>{miCierreData.avisoLegacy}</p>
              </div>
            </div>
          )}

          {/* Tarjeta Principal de Mi Cierre */}
          {miCierreData && !miCierreData.isLegacyOnly && (
            <div className="mi-cierre-main-card">
              <div className="mi-cierre-card-header">
                <div>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 0.25rem 0' }}>
                    Estado de Caja · {fechaMiCierre}
                  </h2>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>
                    Cajero: <strong>{miCierreData.cajeroDisplay || miCierreData.cajeroId}</strong> | Versión: <strong>V{miCierreData.version || 1}</strong>
                  </p>
                </div>

                <div>
                  <span className={`mi-cierre-badge-status ${(miCierreData.estado || '').toLowerCase()}`}>
                    {miCierreData.estado === 'PENDIENTE_CIERRE' ? 'Pendiente de Cierre' : miCierreData.estado}
                  </span>
                </div>
              </div>

              {/* Grid de Totales Financieros */}
              <div className="mi-cierre-grid-totales">
                <div className="mi-cierre-box highlight">
                  <div className="mi-cierre-box-title">Efectivo Esperado en Caja</div>
                  <div className="mi-cierre-box-val" style={{ color: '#15803d' }}>
                    {formatoMoneda(miCierreData.efectivoEsperado)}
                  </div>
                </div>

                <div className="mi-cierre-box">
                  <div className="mi-cierre-box-title">Total Cobrado Registrado</div>
                  <div className="mi-cierre-box-val">{formatoMoneda(miCierreData.totalCobrado)}</div>
                </div>

                <div className="mi-cierre-box accent">
                  <div className="mi-cierre-box-title">No Efectivo (Transf/Dep)</div>
                  <div className="mi-cierre-box-val" style={{ color: '#0369a1' }}>
                    {formatoMoneda(miCierreData.noEfectivoTotal)}
                  </div>
                </div>

                <div className="mi-cierre-box">
                  <div className="mi-cierre-box-title">Pagos Nativos Integrados</div>
                  <div className="mi-cierre-box-val">{miCierreData.paymentCount || 0}</div>
                </div>
              </div>

              {/* Si la caja está CERRADA */}
              {miCierreData.estado === 'CERRADO' ? (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', padding: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Efectivo Declarado al Cerrar:</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
                        {formatoMoneda(miCierreData.efectivoDeclarado)}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Diferencia Resultante:</div>
                      <div className={`diferencia-badge ${(Number(miCierreData.diferenciaEfectivo) === 0 ? 'cuadrado' : Number(miCierreData.diferenciaEfectivo) > 0 ? 'sobrante' : 'faltante')}`}>
                        {Number(miCierreData.diferenciaEfectivo) === 0
                          ? 'CUADRADO ($0.00)'
                          : `${Number(miCierreData.diferenciaEfectivo) > 0 ? 'SOBRANTE +' : 'FALTANTE '}${formatoMoneda(miCierreData.diferenciaEfectivo)}`}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button
                        className="btn-cierre-action primary"
                        onClick={() => handleDescargarComprobante(miCierreData.id)}
                        disabled={!!descargando}
                      >
                        <FileCheck2 size={16} />
                        Ver Acta / Comprobante PDF
                      </button>

                      {puedeReabrirEste(miCierreData) && (
                        <button
                          className="btn-cierre-action"
                          onClick={() => setCierreAReabrir(miCierreData)}
                          style={{ color: '#b91c1c', borderColor: '#fca5a5' }}
                        >
                          <Unlock size={16} />
                          Reabrir cierre
                        </button>
                      )}
                    </div>
                  </div>

                  {miCierreData.observaciones && (
                    <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#334155', background: '#ffffff', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
                      <strong>Observaciones:</strong> {miCierreData.observaciones}
                    </div>
                  )}

                  <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#64748b' }}>
                    Cerrado en: {new Date(miCierreData.cerradoAt).toLocaleString('es-MX')} por {miCierreData.cerradoPor}.
                  </div>
                </div>
              ) : (
                /* Formulario de Captura de Efectivo para Cierre */
                <div className="mi-cierre-form-section">
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, color: '#0f172a' }}>
                    Arqueo de Efectivo Físico
                  </h3>

                  {miCierreData.estado === 'REABIERTO' && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', padding: '0.75rem 1rem', borderRadius: '0.5rem', color: '#991b1b', fontSize: '0.875rem' }}>
                      <strong>Caja Reabierta:</strong> {miCierreData.motivoReapertura || 'Reapertura autorizada'}. Puede capturar o reversar pagos adicionales antes de recerrar.
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', alignItems: 'flex-start' }}>
                    <div className="form-group-cierre">
                      <label className="form-label-cierre">Efectivo Físico Contado / Declarado ($):</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-input-cierre"
                        placeholder="Ej. 5000.00"
                        value={efectivoDeclarado}
                        onChange={(e) => setEfectivoDeclarado(e.target.value)}
                      />
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        Capture el efectivo real existente en caja física. No incluya transferencias.
                      </span>
                    </div>

                    <div className="form-group-cierre">
                      <label className="form-label-cierre">Resultado de Conciliación:</label>
                      <div style={{ height: '48px', display: 'flex', alignItems: 'center' }}>
                        <div className={`diferencia-badge ${estadoDiferencia.toLowerCase()}`}>
                          {estadoDiferencia === 'CUADRADO'
                            ? 'CUADRADO ($0.00)'
                            : `${estadoDiferencia === 'SOBRANTE' ? 'SOBRANTE +' : 'FALTANTE '}${formatoMoneda(decDiferencia)}`}
                        </div>
                      </div>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        Diferencia = Efectivo Declarado - Efectivo Esperado ({formatoMoneda(decEsperado)})
                      </span>
                    </div>
                  </div>

                  <div className="form-group-cierre">
                    <label className="form-label-cierre">
                      Observaciones / Justificación de Incidencias {decDiferencia !== 0 && <span style={{ color: '#dc2626' }}>* (Obligatorio)</span>}
                    </label>
                    <textarea
                      rows={2}
                      className="form-input-cierre"
                      style={{ fontSize: '0.875rem', fontWeight: 400, fontFamily: 'inherit' }}
                      placeholder={decDiferencia !== 0 ? 'Describa el motivo del sobrante o faltante de efectivo...' : 'Comentarios adicionales del cierre...'}
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                    <button
                      className="btn-cierre-action primary"
                      style={{ padding: '0.75rem 1.5rem', fontSize: '0.9375rem' }}
                      disabled={!puedeCerrar}
                      onClick={() => setCierreModalOpen(true)}
                    >
                      <Lock size={16} />
                      {miCierreData.estado === 'REABIERTO' ? 'Recerrar Caja (Nueva Versión)' : 'Cerrar Caja'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal de Confirmación de Cierre */}
      {cierreModalOpen && (
        <div className="cierre-modal-backdrop">
          <div className="cierre-modal-content">
            <div className="cierre-modal-header">
              <AlertTriangle size={24} color="#d97706" />
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>Confirmar Cierre Físico de Caja</h3>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#475569', lineHeight: 1.5 }}>
              Estás por cerrar la caja del <strong>{fechaMiCierre}</strong> con un efectivo declarado de <strong>{formatoMoneda(decDeclarado)}</strong> (Diferencia: <strong>{formatoMoneda(decDiferencia)}</strong>).
            </p>
            <p style={{ fontSize: '0.8125rem', color: '#dc2626', background: '#fee2e2', padding: '0.75rem', borderRadius: '0.5rem' }}>
              <strong>Advertencia de Bloqueo:</strong> Después del cierre no podrás registrar ni reversar pagos de esta fecha hasta que una reapertura sea formalmente autorizada por un supervisor.
            </p>
            <div className="cierre-modal-actions">
              <button className="btn-cierre-action" onClick={() => setCierreModalOpen(false)} disabled={submitting}>
                Cancelar
              </button>
              <button className="btn-cierre-action primary" onClick={ejecutarCerrarCaja} disabled={submitting}>
                {submitting ? 'Cerrando...' : 'Confirmar Cierre'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supervisión de cierres de otros cajeros (solo con CIERRE_CAJA_REABRIR) */}
      {tabActiva === 'MI_CIERRE' && puedeReabrirPermiso && (
        <div className="mi-cierre-container" style={{ marginTop: '1.5rem' }}>
          <div className="mi-cierre-main-card">
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: '0 0 0.25rem', color: '#0f172a' }}>Supervisión de cierres · {fechaMiCierre}</h3>
            <p style={{ fontSize: '0.8125rem', color: '#64748b', margin: '0 0 1rem' }}>
              Cierres persistidos de todos los cajeros para la fecha seleccionada. Un cierre CERRADO se puede reabrir con autorización; queda auditado.
            </p>
            {supervision.cargando ? (
              <div style={{ color: '#64748b', fontSize: '0.875rem' }}>Cargando cierres…</div>
            ) : supervision.lista.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: '0.875rem' }}>No hay cierres registrados para esta fecha.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="cierre-table" style={{ width: '100%', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr>
                      <th>Folio</th><th>Cajero</th><th>Versión</th><th>Estado</th>
                      <th style={{ textAlign: 'right' }}>Esperado</th><th style={{ textAlign: 'right' }}>Declarado</th><th style={{ textAlign: 'right' }}>Diferencia</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {supervision.lista.map((c) => (
                      <tr key={c.id}>
                        <td>{c.folioCierre}</td>
                        <td>{c.cajeroDisplay || c.cajeroId}</td>
                        <td>V{c.version}</td>
                        <td>{c.etiquetaEstado || c.estado}</td>
                        <td style={{ textAlign: 'right' }}>{formatoMoneda(c.efectivoEsperado)}</td>
                        <td style={{ textAlign: 'right' }}>{formatoMoneda(c.efectivoDeclarado)}</td>
                        <td style={{ textAlign: 'right' }}>{formatoMoneda(c.diferenciaEfectivo)}</td>
                        <td style={{ textAlign: 'right' }}>
                          {puedeReabrirEste(c) && (
                            <button className="btn-cierre-action" style={{ color: '#b91c1c', borderColor: '#fca5a5' }} onClick={() => setCierreAReabrir(c)}>
                              <Unlock size={14} /> Reabrir cierre
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Reapertura (P08-B0.5) */}
      {cierreAReabrir && (
        <div className="cierre-modal-backdrop" onClick={() => !submitting && setCierreAReabrir(null)}>
          <div className="cierre-modal-content" role="dialog" aria-labelledby="reabrir-titulo" onClick={(e) => e.stopPropagation()}>
            <div className="cierre-modal-header">
              <Unlock size={24} color="#dc2626" />
              <h3 id="reabrir-titulo" style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>Reabrir cierre</h3>
            </div>
            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.25rem 1rem', fontSize: '0.875rem', margin: '0 0 0.75rem' }}>
              <dt style={{ color: '#64748b' }}>Cierre</dt><dd style={{ margin: 0, fontWeight: 600 }}>{cierreAReabrir.folioCierre || cierreAReabrir.id}</dd>
              <dt style={{ color: '#64748b' }}>Fecha</dt><dd style={{ margin: 0 }}>{String(cierreAReabrir.fechaOperacion || fechaMiCierre).slice(0, 10)}</dd>
              <dt style={{ color: '#64748b' }}>Cajero</dt><dd style={{ margin: 0 }}>{cierreAReabrir.cajeroDisplay || cierreAReabrir.cajeroId}</dd>
              <dt style={{ color: '#64748b' }}>Efectivo esperado</dt><dd style={{ margin: 0 }}>{formatoMoneda(cierreAReabrir.efectivoEsperado)}</dd>
              <dt style={{ color: '#64748b' }}>Efectivo declarado</dt><dd style={{ margin: 0 }}>{formatoMoneda(cierreAReabrir.efectivoDeclarado)}</dd>
              <dt style={{ color: '#64748b' }}>Diferencia</dt><dd style={{ margin: 0 }}>{formatoMoneda(cierreAReabrir.diferenciaEfectivo)}</dd>
            </dl>
            <p style={{ fontSize: '0.8125rem', color: '#991b1b', background: '#fef2f2', padding: '0.75rem', borderRadius: '0.5rem', margin: '0 0 0.75rem' }}>
              Reabrir este cierre habilitará correcciones autorizadas sobre los movimientos incluidos. La operación quedará auditada.
            </p>
            <div className="form-group-cierre">
              <label className="form-label-cierre" htmlFor="reabrir-motivo">Motivo de reapertura <span style={{ color: '#dc2626' }}>*</span></label>
              <textarea
                id="reabrir-motivo"
                rows={3}
                maxLength={500}
                className="form-input-cierre"
                style={{ fontSize: '0.875rem', fontWeight: 400 }}
                placeholder="Indique el motivo por el cual se autoriza la reapertura..."
                value={motivoReapertura}
                onChange={(e) => setMotivoReapertura(e.target.value)}
              />
            </div>
            <div className="form-group-cierre">
              <label className="form-label-cierre" htmlFor="reabrir-observaciones">Observaciones (opcional)</label>
              <textarea
                id="reabrir-observaciones"
                rows={2}
                maxLength={400}
                className="form-input-cierre"
                style={{ fontSize: '0.875rem', fontWeight: 400 }}
                value={observacionesReapertura}
                onChange={(e) => setObservacionesReapertura(e.target.value)}
              />
            </div>
            <div className="cierre-modal-actions">
              <button className="btn-cierre-action" onClick={() => setCierreAReabrir(null)} disabled={submitting}>
                Cancelar
              </button>
              <button
                className="btn-cierre-action"
                style={{ background: '#dc2626', color: '#fff', borderColor: '#dc2626' }}
                onClick={ejecutarReabrirCaja}
                disabled={submitting || motivoReapertura.trim().length < 3}
              >
                {submitting ? 'Autorizando...' : 'Reabrir cierre'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
